// POST configs
// PUT  /configs/:id
// DELETE /configs/:id
// GET /configs
// GET /configs/:id
// POST /query    MEA query with workflows for each entity
// POST /createwftestdata
// POST /createworkflowinstances
// POST /sendworkflownotifications
// POST /commentnotification
// POST /deleteworkflowinstances
// GET /roles/:id
// POST /escalate    {userId: '', wfInstanceId: '', message: ''}
// GET /role/:userid?
// POST /delegate
// GET /delegateusers/:id
// GET /instances
// GET /instances/:id
// POST /directupdate
// POST /validateconfig
// POST /instances     <- { meaId, period, series, parameters }
// POST /validatecreateparams
// GET //modifyparams/:id
// POST /validatemodifyparams
// PUT /instances/:id
// DELETE /instances/:id
// POST /measurevalues
import axios from "axios";
import {
  CreateAttachmentsParams,
  CreateCommentParams,
  DeleteCommentParams,
  FileValue,
  GETWorkflowInstanceResult,
  GETWorkflowInstanceResultData,
  GetAllCommentsParams,
  GetCommentParams,
  MeasurePayload,
  MyTasksData,
  MyTasksParams,
  MyWorkflowsParams,
  TWorkflowQueryResult,
  UpdateCommentsParams,
  WFComment,
  WFConfigParams,
  WFConfigParamsServer,
  WFConfiguration,
  WFDelegateParams,
  WFDelegateUser,
  WFEscalateParams,
  WFInstanceChangeData,
  WFRole,
  WFRoleParams,
  WFStepRole,
  WFTestInstanceParams,
  WFUpdateInstanceState,
  WorkflowLogRow,
} from "./types";
const workflowApi = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/api/workflow`,
});
const commentsApi = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/api/mea`,
});
const baseAPI = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/api/`,
});

// POST configs
// PUT  /configs/:id
// DELETE /configs/:id
// GET /configs
// GET /configs/:id

/**
 * Create a new Workflow Configuration
 *
 * @param params
 * @returns
 */
export const createWorkflowConfiguration = (params?: WFConfigParams) =>
  workflowApi
    .post<any>("/configs", {
      ...params,
      configuration: JSON.stringify(params?.configuration),
    })
    .then((res) => res.data.data);

/**
 * Fetch all configurations
 *
 * @param params
 * @returns
 */
export const getWorkflowConfigurations = () =>
  workflowApi
    .get<{ data: WFConfigParamsServer[] }>("/configs", {})
    .then<WFConfigParams[]>((res) =>
      res.data.data.map((value) => {
        return {
          ...value,
          configuration: JSON.parse(value.configuration) as WFConfiguration,
        };
      })
    );

/**
 * Fetch all User Roles, which are defined in the MongoDB roles collection
 *
 * GET /api/roles { data: WFRoles[] }
 *
 * @param params
 * @returns
 */
export const getUserRoles = () =>
  baseAPI.get<{ data: WFRole[] }>("/roles", {}).then((res) => res.data);

/**
 * Update configuration entry
 *
 * @param params
 * @returns
 */
export const updateWorkflowConfiguation = (params: WFConfigParams) => {
  const id = params._id;
  delete params._id;
  return workflowApi
    .put<any>(`/configs/${id}`, {
      ...params,
      configuration: JSON.stringify(params.configuration),
    })
    .then<WFConfigParams[]>((res) => res.data);
};

/**
 * Delete configuration entry
 *
 * @param params
 * @returns
 */
export const deleteWorkflowConfiguration = (id: string) => {
  return workflowApi
    .delete<any>(`/configs/${id}`, {})
    .then<WFConfigParams[]>((res) => res.data);
};

/**
 * Create a new role entry
 *
 * @param params
 * @returns
 */
export const createNewRole = (params: WFRoleParams) => {
  return baseAPI
    .post<any>(`/roles`, params)
    .then<WFConfigParams[]>((res) => res.data);
};

/**
 * Update role entry
 *
 * @param params
 * @returns
 */
export const updateRole = (id: string, params: WFRoleParams) => {
  return baseAPI
    .put<any>(`/roles/${id}`, params)
    .then<WFConfigParams[]>((res) => res.data);
};

/**
 * Delete role entry
 *
 * @param params
 * @returns
 */
export const deleteRole = (id: string) => {
  return baseAPI
    .delete<any>(`/roles/${id}`, {})
    .then<WFConfigParams[]>((res) => res.data);
};

/**
 * Fetch list of mytasks
 *
 * @param params
 * @returns
 */
export const getMyTasks = (params?: MyTasksParams) =>
  workflowApi
    .get<{ data: MyTasksData[] }>("/mytasks", { params })
    .then((res) => res.data.data);

/**
 *
 * Fetches the Workflow declarations for speficic Element IDs
 *
 * @param params
 * @returns
 */
export const queryMyWorkflows = (params?: MyWorkflowsParams) =>
  workflowApi
    .post<{ data: TWorkflowQueryResult[] }>("/myworkflows", params)
    .then((res) => res.data.data);

/**
 * Get Workflow Instance by id
 *
 * @param id
 * @returns
 */
export const getInstance = (id: string, includeConfig?: boolean) =>
  workflowApi
    .get<GETWorkflowInstanceResult>(
      `/instances/${id}${includeConfig ? "?includeConfig=true" : ""}`,
      {}
    )
    .then((res) => res.data.data);

/**
 * Get All Workflow Instances (admin only)
 *
 * @param id
 * @returns
 */
export const getAllInstances = (type: string, period: string) =>
  workflowApi
    .get<{ data: GETWorkflowInstanceResultData[] }>(`/instances`, {
      params: { type, period },
    })
    .then((res) => res.data.data);

/**
 * Delete Workflow Instance by id
 *
 * @param id
 * @returns
 */
export const deleteWorkflowInstance = (id: string) =>
  workflowApi
    .delete<GETWorkflowInstanceResult>(`/instances/${id}`, {})
    .then((res) => res.data.data);

/**
 * Get logs for a specific Workflow element
 *
 * @param id
 * @returns
 */
export const getLogs = (id: string) =>
  workflowApi
    .get<{ data: WorkflowLogRow[] }>(`/logs/${id}`, {})
    .then((res) => res.data.data);

/**
 * Get logs for a specific Workflow element
 *
 * @param id
 * @returns
 */
export const getWorkflowRoles = (id: string) =>
  workflowApi
    .get<{ data: WFStepRole[] }>(`/roles/${id}`, {})
    .then((res) => res.data.data);

/**
 * Update Workflow Instance
 *
 * @param id
 * @returns
 */
export const putInstances = (id: string) =>
  workflowApi
    .put<WFInstanceChangeData>("/instances", { params: { id } })
    .then((res) => res.data);

// POST /measurevalues

/**
 *
 * This endpoint is used to send new element values to the workflow engine.
 *
 * @param params
 * @returns
 */
export const postMeasureValues = (params: MeasurePayload) =>
  workflowApi.post<{}>("/measurevalues", params).then((res) => res.data);

/**
 *
 * Start Workflow Instances for a certain period for all measures
 *
 * @param params
 * @returns
 */
export const createWorkflowInstances = (params: { period: string }) =>
  workflowApi
    .post<{}>("/createworkflowinstances", params)
    .then((res) => res.data);

/**
 *
 * Start Workflow Instances for a certain period for all measures
 *
 * @param params
 * @returns
 */
export const createWFTestIntances = (params: WFTestInstanceParams) =>
  workflowApi.post<{}>("/createwftestdata", params).then((res) => res.data);

/**
 *
 * Update WF instance state
 *
 * @param params
 * @returns
 */
export const updateWorkflowInstanceState = (params: WFUpdateInstanceState) =>
  workflowApi
    .put<{}>(`/instances/${params.id}`, params.transition)
    .then((res) => res.data);

/**
 * POST /mea/comments/
 * Create a new comment for element
 *
 * @param params
 * @returns
 */
export const createComment = (params: CreateCommentParams) => {
  params.publishtype = params.publishtype || "Parent element users";
  commentsApi.post<any>("/comments", params).then((res) => res.data);
};

/**
 * GET /mea/comments/?parentId=xxx&period=xxx
 * Fetch comment for creating period using element ID
 *
 * @param params
 * @returns
 */
export const getComment = (params: GetCommentParams) =>
  commentsApi.get<WFComment[]>("/comments", { params }).then((res) => res.data);

/**
 * GET /mea/comments/?period=xxx
 * Fetch all comments using element ID
 *
 * @param params
 * @returns
 */
export const getAllComments = (params: GetAllCommentsParams) =>
  commentsApi.get<any>("/comments", { params }).then((res) => res.data);

/**
 * PUT /mea/comments/:id
 * Update Comment using the comment ID
 *
 * @param params
 * @returns
 */
export const updateComment = (params: UpdateCommentsParams) =>
  workflowApi.put<any>(`/comments/${params.commentId}`, {
    comment: params.comment,
    wfId: params.wfId,
  });

/**
 * DELETE /mea/comments/:id
 * Delete a Comment using the comment ID
 *
 * @param params
 * @returns
 */
export const deleteComment = (params: DeleteCommentParams) =>
  commentsApi
    .delete<any>(`/comments/${params.commentId}`)
    .then((res) => res.data);

// Files endpoint

/**
 * POST  /attachments Create attachments
 * Add embedded files to Portal Actions
 *
 * @param params
 * @returns
 */

export const createAttachments = (params: CreateAttachmentsParams) => {
  const formData = new FormData();
  params.files.forEach((file) => formData.append("files", file, file.name));
  formData.append("parentId", params.parentId);
  formData.append("period", params.period);
  return baseAPI.post<any>("/attachments", formData).then((res) => res.data);
};

/**
 * POST  /attachments Create attachments
 * Add embedded files to Portal Actions
 *
 * @param params
 * @returns
 */

export const getAttachments = (params: {
  period: string;
  parentId: string;
}) => {
  return baseAPI
    .get<FileValue[]>("/attachments", { params })
    .then((res) => res.data);
};

/**
 * Escalation and Delegation
 */

// GET /roles/:id
// POST /escalate    {userId: '', wfInstanceId: '', message: ''}
// GET /role/:userid?
// POST /delegate
// GET /delegateusers/:id

/**
 * POST /workflow/escalate/
 * Escalate worfklow for some user
 *
 * @param params
 * @returns
 */
export const escalate = (params: WFEscalateParams) =>
  workflowApi.post<any>("/escalate", params).then((res) => res.data);

/**
 * POST /workflow/delegate/
 * Create a new comment for element
 *
 * @param params
 * @returns
 */
export const delegate = (params: WFDelegateParams) =>
  workflowApi.post<any>("/delegate", params).then((res) => res.data);

/**
 * GET /workflow/delegateusers/:id
 * Fetch users to which the workflow is able to delegate to
 *
 * @param params
 * @returns
 */
export const getDelegateUsers = (id: string) =>
  workflowApi
    .get<{ data: WFDelegateUser[] }>(
      `/delegateusers/${id}?requireSameRole=false`,
      {}
    )
    .then((res) => res.data.data);
